/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui icons
import Check from '@material-ui/icons/Check';
// @material-ui/core components
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { locationScopes } from 'config';
// our components
import adCreateStyles from 'assets/jss/AdsCreate';
import { PROMOTED_AD_PACKAGES } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import CategorySelect from 'components/common/CategorySelect';
import LocationSelect from 'components/common/LocationSelect';
import DurationSelect from 'components/common/DurationSelect';
import qs from 'helpers/queryStrings';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */

const translateValidationSchema = t =>
  Yup.object({
    categories: Yup.string(),
    subcategories: Yup.string(),
    provinces: Yup.string(),
    municipalities: Yup.string(),
    locationScope: Yup.string().required(t('errors.required')),
    duration: Yup.object().required(t('errors.required')),
    price: Yup.number(t('errors.numbers'))
      .min(0, '')
      .required(t('errors.required')),
  });

class BulkUpdateCreatePromotedAdPackagesForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { appliedFilter, durations } = props;

    let { locationScope } = appliedFilter;
    if (appliedFilter.province && appliedFilter.municipality) {
      locationScope = locationScopes.MUNICIPAL;
    } else if (appliedFilter.province) {
      locationScope = locationScopes.PROVINCIAL;
    } else {
      locationScope = locationScopes.NATIONAL;
    }
    this.state = {
      fields: {
        price: undefined,
        duration: appliedFilter.duration || durations[0],
        categories: appliedFilter.category || undefined,
        subcategories: appliedFilter.subcategory || undefined,
        provinces: appliedFilter.province || undefined,
        municipalities: appliedFilter.municipality || undefined,
        locationScope,
      },
    };
    this.formikRef = null;
    moment.locale(props.i18n.language);
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(PROMOTED_AD_PACKAGES.LIST);
  };

  /**
   * @description
   * Updating before send to parent.
   *
   * @param {*} values - New values to update fields.
   * @returns {*} Undefined - No returns.
   */
  handleSubmit = values => {
    const history = this.context;
    const { onSave } = this.props;
    const finalValues = {
      ...values,
    };

    const currentLocation = history.location.pathname;
    const stringified = qs.stringify({
      category: finalValues.categories,
      subcategory: finalValues.subcategories,
      province: finalValues.provinces,
      municipality: finalValues.municipalities,
      locationScope: finalValues.locationScope,
      duration: finalValues.duration,
    });
    history.push({ pathname: currentLocation, search: stringified });

    if (finalValues.subcategories) {
      delete finalValues.categories;
    }

    if (finalValues.municipalities) {
      delete finalValues.provinces;
    }

    return onSave(finalValues);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   *
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  render() {
    const {
      classes,
      errors: serverErrors,
      submitLoading,
      t,
      closeNotification,
      categories,
      provinces,
      durations,
    } = this.props;
    const { fields } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);

    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          closeNotification={closeNotification}
          close
        />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={fields}
          validationSchema={validationSchema}
          onSubmit={values => this.handleSubmit(values)}
          render={({ values, errors, touched, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className={classes.pageContainer}>
                <div
                  className={`${classes.row} ${classes.contentBtns}`}
                  style={{ justifyContent: 'space-between' }}
                >
                  <h3>{t('promoted_ad_packages.form.title')}</h3>
                </div>
                <Card>
                  <CardBody>
                    <Loading show={submitLoading} />
                    <GridContainer>
                      <GridItem xs={12} md={6}>
                        <SectionDivider
                          label={t('promoted_ad_packages.form.fields.category')}
                        >
                          <div className={classes.categorySection}>
                            <CategorySelect
                              appliedFilter={{
                                category: values.categories,
                                subcategory: values.subcategories,
                              }}
                              changeFilter={changedData => {
                                handleChange({
                                  target: {
                                    name: 'categories',
                                    value:
                                      'category' in changedData
                                        ? changedData.category
                                        : values.categories,
                                  },
                                });
                                handleChange({
                                  target: {
                                    name: 'subcategories',
                                    value: changedData.subcategory,
                                  },
                                });
                              }}
                              categories={categories}
                            />
                          </div>
                        </SectionDivider>
                        <SectionDivider
                          label={t('promoted_ad_packages.form.fields.location')}
                        >
                          <div className={classes.categorySection}>
                            <LocationSelect
                              provinces={provinces}
                              selectedProvince={values.provinces}
                              selectedMunicipality={values.municipalities}
                              changeFilter={({ province, municipality }) => {
                                if (typeof province !== 'undefined') {
                                  if (
                                    values.locationScope ===
                                      locationScopes.NATIONAL &&
                                    province !== 0
                                  ) {
                                    handleChange({
                                      target: {
                                        name: 'locationScope',
                                        value: locationScopes.PROVINCIAL,
                                      },
                                    });
                                  }
                                  handleChange({
                                    target: {
                                      name: 'provinces',
                                      value:
                                        province !== 0 ? province : undefined,
                                    },
                                  });
                                  handleChange({
                                    target: {
                                      name: 'municipalities',
                                      value: undefined,
                                    },
                                  });
                                }
                                if (typeof municipality !== 'undefined') {
                                  if (
                                    values.locationScope ===
                                      locationScopes.PROVINCIAL &&
                                    municipality !== 0
                                  ) {
                                    handleChange({
                                      target: {
                                        name: 'locationScope',
                                        value: locationScopes.MUNICIPAL,
                                      },
                                    });
                                  }
                                  handleChange({
                                    target: {
                                      name: 'municipalities',
                                      value:
                                        municipality !== 0
                                          ? municipality
                                          : undefined,
                                    },
                                  });
                                }
                              }}
                            />
                          </div>
                        </SectionDivider>
                        <SectionDivider
                          label={t('promoted_ad_packages.form.fields.duration')}
                        >
                          <div className={classes.categorySection}>
                            <DurationSelect
                              durations={durations}
                              selectedDuration={values.duration}
                              change={({ duration }) =>
                                handleChange({
                                  target: {
                                    name: 'duration',
                                    value: duration,
                                  },
                                })
                              }
                            />
                          </div>
                        </SectionDivider>
                      </GridItem>
                      <GridItem xs={12} md={6}>
                        <SectionDivider
                          label={t(
                            'promoted_ad_packages.form.fields.locationScope',
                          )}
                        >
                          <div style={{ padding: 10 }}>
                            <GridContainer>
                              <GridItem xs={12} md={12}>
                                <FormControlLabel
                                  control={
                                    <Radio
                                      disabled={!!values.provinces}
                                      onChange={() =>
                                        handleChange({
                                          target: {
                                            name: 'locationScope',
                                            value: locationScopes.NATIONAL,
                                          },
                                        })
                                      }
                                      checked={
                                        values.locationScope ===
                                        locationScopes.NATIONAL
                                      }
                                      checkedIcon={
                                        <Check
                                          className={classes.radioChecked}
                                        />
                                      }
                                      icon={
                                        <Check
                                          className={classes.radioUnchecked}
                                        />
                                      }
                                      className={classes.radio}
                                    />
                                  }
                                  id="nationalRadio"
                                  label={t(
                                    'promoted_ad_packages.filters.location_scope.national',
                                  )}
                                  style={{ width: '100%' }}
                                />
                                <FormControlLabel
                                  control={
                                    <Radio
                                      disabled={!!values.municipalities}
                                      onChange={() =>
                                        handleChange({
                                          target: {
                                            name: 'locationScope',
                                            value: locationScopes.PROVINCIAL,
                                          },
                                        })
                                      }
                                      checked={
                                        values.locationScope ===
                                        locationScopes.PROVINCIAL
                                      }
                                      checkedIcon={
                                        <Check
                                          className={classes.radioChecked}
                                        />
                                      }
                                      icon={
                                        <Check
                                          className={classes.radioUnchecked}
                                        />
                                      }
                                      className={classes.radio}
                                    />
                                  }
                                  id="provincialRadio"
                                  label={t(
                                    'promoted_ad_packages.filters.location_scope.provincial',
                                  )}
                                  style={{ width: '100%' }}
                                />
                                <FormControlLabel
                                  control={
                                    <Radio
                                      onChange={() =>
                                        handleChange({
                                          target: {
                                            name: 'locationScope',
                                            value: locationScopes.MUNICIPAL,
                                          },
                                        })
                                      }
                                      checked={
                                        values.locationScope ===
                                        locationScopes.MUNICIPAL
                                      }
                                      checkedIcon={
                                        <Check
                                          className={classes.radioChecked}
                                        />
                                      }
                                      icon={
                                        <Check
                                          className={classes.radioUnchecked}
                                        />
                                      }
                                      className={classes.radio}
                                    />
                                  }
                                  id="municipalRadio"
                                  label={t(
                                    'promoted_ad_packages.filters.location_scope.municipal',
                                  )}
                                  style={{ width: '100%' }}
                                />
                              </GridItem>
                            </GridContainer>
                          </div>
                        </SectionDivider>
                      </GridItem>
                      <GridItem xs={12} md={12}>
                        <TextField
                          label={t('promoted_ad_packages.form.fields.price')}
                          name="price"
                          error={touched.price && !!errors.price}
                          helperText={touched.price ? errors.price : ''}
                          InputProps={{
                            value: values.price,
                            onChange: e =>
                              handleChange({
                                target: {
                                  name: 'price',
                                  value: e.target.value,
                                },
                              }),
                            type: 'number',
                          }}
                        />
                      </GridItem>
                    </GridContainer>
                    <div className={classes.section}>
                      <GridContainer direction="row-reverse">
                        <GridItem xs={12} sm={6}>
                          <div className={classes.contentBtns}>
                            <Button
                              id="save-packages"
                              color="primary"
                              type="submit"
                            >
                              {t('paidAd.form.button_create')}
                            </Button>
                          </div>
                        </GridItem>
                        <GridItem xs={12} sm={6}>
                          <Button
                            id="cancel-create"
                            simple
                            color="danger"
                            onClick={this.handleCancelBtn}
                          >
                            {t('common.cancel')}
                          </Button>
                        </GridItem>
                      </GridContainer>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </form>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * AdForm PropTypes
 * @param classes - styles received from withStyles
 * @param errors - Errors data list
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with language information
 * @param appliedFilter - prop initial Filters to apply
 * @param categories - prop know categories and subcategories
 * @param provinces - prop know provinces and municipalities
 * @param durations - prop know durations
 * @callback onSave - callback called when user clicks save button
 */

BulkUpdateCreatePromotedAdPackagesForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  closeNotification: PropTypes.func.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  durations: PropTypes.instanceOf(Array).isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const BulkUpdateCreatePromotedAdPackagesFormWrapper = props => (
  <BulkUpdateCreatePromotedAdPackagesForm {...props} />
);

export default withTranslation()(
  withStyles(adCreateStyles)(BulkUpdateCreatePromotedAdPackagesFormWrapper),
);
